import _ from 'lodash';
import { BudgetPhases } from 'business/constants';
import { PROJECT_ID } from 'routes/BudgetModule/config/constants';
import {
    BudgetProposalT,
    InscriptionWithMetadata,
    FieldSettingsT,
    StatusesSettingsT,
} from 'routes/BudgetModule/config/types';
import {
    ColDef,
    ColumnMenuTab,
    IAggFuncParams,
    ValueFormatterParams,
    ValueGetterParams,
} from 'ag-grid-community';
import {
    isInscriptionRejected,
    statusComparator,
} from 'routes/BudgetModule/helpers/statusHelpers';
import {
    getInscriptionField,
    getInscriptionFieldValueForParentBPPhase,
    getBudgetProposalFromInscription,
    InscriptionT,
    InscriptionGenericPhaseFieldT,
    isOrphan,
    getInscriptionFieldValueForPhase,
    PhaseFieldValueT,
} from 'routes/BudgetModule/shared/entities/inscription';
import {
    getFieldForPhase,
    getIsEditablePhase,
    phaseComparator,
} from 'routes/BudgetModule/shared/phases/helpers';
import { startAdornment } from 'ui/AgGridReact/CurrencyInputAdornment';
import { translateBlockValue } from 'i18n';
import { parseNumericInput } from 'helpers/UIHelpers';
import NumberFormatCustom from 'components/NumberFormat/NumberFormatCustom';
import { Project } from 'routes/BudgetModule/shared/useGetProjects';
import {
    inscriptionTextComparator,
    getOriginalOrCurrentInscriptionValue,
} from './dataHelpers';
import { OpenButtonCellRendererParams } from './cellRenderers/OpenButtonCellRenderer';
import { SeeDetailsCellRendererParams } from './cellRenderers/SeeDetailsCellRenderer';
import { StatusCellRendererParams } from './cellRenderers/StatusCellRenderer';
import { TextFieldCellRendererParams } from './cellRenderers/TextFieldCellRenderer';
import { getColumnSettings } from '../../BudgetProposal/Resource/ExpenseIncome/Imputations/helpers/gridHelpers';
import { CURRENCY_EXCEL_CLASSNAMES } from 'routes/BudgetModule/config/excelExportHelpers';

export type InscriptionsGridDataT = { inscription: InscriptionT };

/**
 * Get the budget phase for a given inscription and budget proposals.
 * If a specific phase is provided, it returns that phase. Otherwise, it
 * gets the phase from the inscription and budget proposals.
 */
const getBugdetPhase = (
    inscription: InscriptionT,
    budgetProposals: BudgetProposalT[],
    phase?: BudgetPhases,
): BudgetPhases =>
    phase
        ? phase
        : getBudgetProposalFromInscription(inscription, budgetProposals).phase;

/**
 * Extract a phase field value from an inscription and a given phase.
 * If a phase is not provided, the phase used is the parent budget proposal one.
 */
const getInscriptionPhaseFieldValue = (
    inscription: InscriptionT,
    field: InscriptionGenericPhaseFieldT,
    budgetProposals: BudgetProposalT[],
    phase?: BudgetPhases,
): PhaseFieldValueT =>
    getInscriptionFieldValueForPhase(
        inscription,
        getBugdetPhase(inscription, budgetProposals, phase),
        field,
    );

/**
 * Return if an inscription is rejected on a given phase.
 * If a phase is not provided, the phase used is the parent budget proposal one.
 */
const isRejectedStatus = (
    inscription: InscriptionT,
    statusesSettings: StatusesSettingsT,
    budgetProposals: BudgetProposalT[],
    phase?: BudgetPhases,
): boolean => {
    const inscriptionPhase = getBugdetPhase(
        inscription,
        budgetProposals,
        phase,
    );
    return isInscriptionRejected(
        inscription,
        getFieldForPhase(inscriptionPhase, 'status') || '',
        inscriptionPhase,
        statusesSettings,
    );
};

/**
 * A col definition that returns an inscription phase field value to be passed into a aggrid cell renderer.
 * The format of the value is different when rows are grouped.
 */
const inscriptionPhaseFieldValueGetter = (
    params: ValueGetterParams,
    statusesSettings: StatusesSettingsT,
    budgetProposals: BudgetProposalT[],
    phase?: BudgetPhases,
): PhaseFieldValueT | { text: PhaseFieldValueT; isRejected: boolean } => {
    if (!params.data) return undefined;

    if (params.column?.isRowGroupActive()) {
        return getInscriptionPhaseFieldValue(
            params.data.inscription,
            params.colDef.field as InscriptionGenericPhaseFieldT,
            budgetProposals,
            phase,
        );
    }

    return {
        text: getInscriptionPhaseFieldValue(
            params.data.inscription,
            params.colDef.field as InscriptionGenericPhaseFieldT,
            budgetProposals,
            phase,
        ),
        isRejected: isRejectedStatus(
            params.data.inscription,
            statusesSettings,
            budgetProposals,
            phase,
        ),
    };
};

export const getAmountAggData =
    (
        budgetProposals: BudgetProposalT[],
        statusesSettings: StatusesSettingsT,
    ): FlowAnyFunction =>
    (params: IAggFuncParams): number => {
        if (!params.rowNode.childrenAfterFilter) return 0;
        const nodes = params.rowNode.childrenAfterFilter?.map(
            (node) => node.data || node.aggData,
        );

        return _.round(
            _.sumBy(
                nodes,
                (
                    inscriptionWithRights: InscriptionWithMetadata & {
                        amount?: number;
                    },
                ): number => {
                    // If the data comes from node.aggData, it only contains the 'amount' field
                    // corresponding to this column
                    if (
                        'amount' in inscriptionWithRights &&
                        inscriptionWithRights.amount !== undefined
                    ) {
                        return inscriptionWithRights.amount;
                    }
                    const ins = inscriptionWithRights.inscription;

                    if (!ins) return 0;

                    const statusField = getInscriptionField(
                        ins,
                        budgetProposals,
                        'status',
                    );
                    const { phase } = getBudgetProposalFromInscription(
                        ins,
                        budgetProposals,
                    );

                    // We remove rejected inscriptions from the sum
                    if (
                        isInscriptionRejected(
                            ins,
                            statusField,
                            phase,
                            statusesSettings,
                        )
                    )
                        return 0;

                    // @ts-expect-error [kill old Ag Grid types] This function should return a number
                    return getInscriptionFieldValueForParentBPPhase(
                        ins,
                        budgetProposals,
                        params.colDef.field as InscriptionGenericPhaseFieldT,
                    );
                },
            ),
            2,
        );
    };

export const getStatusColumn = (
    t: TranslationT,
    budgetProposals: BudgetProposalT[],
    phase?: BudgetPhases,
): ColDef => ({
    field: 'status',
    headerName: t('common.status'),
    cellRenderer: 'statusCellRenderer',
    valueFormatter: ({ value }) => translateBlockValue(value),
    valueGetter: ({ data, colDef }: ValueGetterParams) => {
        if (!data) return '';
        return getInscriptionPhaseFieldValue(
            data.inscription,
            colDef.field as InscriptionGenericPhaseFieldT,
            budgetProposals,
            phase,
        );
    },

    width: 175,
    comparator: statusComparator,
    filter: 'agTextColumnFilter',
    floatingFilterComponent: 'textFloatingFilter',
    filterValueGetter: ({ data, colDef }: ValueGetterParams) => {
        if (!data) return '';
        if (!phase)
            return t(
                `sentence:budgetProposal.${getInscriptionFieldValueForParentBPPhase(
                    data?.inscription,
                    budgetProposals,
                    colDef.field as InscriptionGenericPhaseFieldT,
                )}`,
            );
        return t(
            `sentence:budgetProposal.${getInscriptionFieldValueForPhase(
                data?.inscription,
                phase,
                colDef.field as InscriptionGenericPhaseFieldT,
            )}`,
        );
    },

    enableRowGroup: true,
    sortable: true,
});

const getImputationColumn = (
    t: TranslationT,
    imputationAsString: null | ((arg1: InscriptionT) => string),
): ColDef => ({
    field: 'imputation',
    headerName: t('budgetProposal.imputation'),
    sortable: true,
    width: 200,

    valueGetter: ({ data }: { data?: InscriptionsGridDataT }) => {
        if (!imputationAsString || (data && isOrphan(data.inscription)))
            return t(`budgetProposal.toClassify`);
        if (!data) return '';
        return imputationAsString(data.inscription);
    },

    cellRenderer: 'textHoverCellRenderer',
    filter: 'agTextColumnFilter',
    floatingFilterComponent: 'textFloatingFilter',
    enableRowGroup: true,
});

/*
 * Return a string concatenation of all projects names related to the given inscription.
 */
const getProjectsNames = (
    inscriptionProjectsIds: number[],
    projects: Project[],
) =>
    projects
        .filter((project) => inscriptionProjectsIds.includes(project.id))
        .map((project) => project.name)
        .join(' ');

/*
 * Compare alphabetically two project ids based on their associated names.
 */
const projectComparator = (
    a: string | number[],
    b: string | number[],
    projects: Project[],
) => {
    if (a.length === 0) return 1;
    if (b.length === 0) return -1;
    const projectIdsA = Array.isArray(a) ? a : [+a];
    const projectIdsB = Array.isArray(b) ? b : [+b];
    return getProjectsNames(projectIdsA, projects).localeCompare(
        getProjectsNames(projectIdsB, projects),
    );
};

export const getProjectColumn = (
    t: TranslationT,
    projects: Project[],
): ColDef => ({
    field: PROJECT_ID,
    headerName: t('budgetProposal.projects'),
    cellRenderer: 'projectsCellRenderer',
    suppressHeaderMenuButton: true,
    editable: false,
    suppressNavigable: true,
    valueGetter: ({ data }: { data?: InscriptionsGridDataT }) =>
        data?.inscription[PROJECT_ID],
    valueFormatter: ({ data }: { data?: InscriptionsGridDataT }) => {
        if (!data) return '';
        return getProjectsNames(data?.inscription[PROJECT_ID], projects);
    },
    width: 230,
    filter: 'agTextColumnFilter',
    floatingFilterComponent: 'textFloatingFilter',
    filterValueGetter: ({ data }: { data?: InscriptionsGridDataT }) => {
        if (!data) return '';
        return getProjectsNames(data?.inscription[PROJECT_ID], projects);
    },
    suppressColumnsToolPanel: true,
    sortable: true,
    resizable: false,
    enableRowGroup: true,
    suppressMovable: true,
    comparator: (a, b) => projectComparator(a, b, projects),
});

const getActionsColumn = (budgetProposals: BudgetProposalT[]): ColDef => ({
    field: 'actions',
    headerName: '',
    cellRenderer: 'actionsCellRenderer',
    suppressHeaderMenuButton: true,
    editable: false,
    suppressNavigable: true,

    valueGetter: ({ data }: { data: InscriptionsGridDataT }) => {
        if (!data) return null;
        const amountOrCommentUpdated = _.intersection(_(data).keys().value(), [
            'amount',
            'comment',
        ]) as ('amount' | 'comment')[];
        // In order to have fresh value when changing the amount or the column, we need to trigger a rerendering.
        // The rerendering of the column is done by aggrid when it detects a change in the value. Action column doesn't have
        // any values set so it doesn't rerender the column when the comment or the amount is changed.
        // Since we want this values to be fresh in order to pass them to the moveInscription modal,
        // we trigger a rerendering by setting the comment or action value passed to the column whenever it is updated.
        if (amountOrCommentUpdated.length > 0)
            return getInscriptionFieldValueForParentBPPhase(
                data?.inscription,
                budgetProposals,
                amountOrCommentUpdated[0],
            );
        return null;
    },

    width: 60,
    suppressColumnsToolPanel: true,
    resizable: false,
    suppressMovable: true,
});

export const getDetailsColumn = (
    allowInscriptionSelection: boolean,
): ColDef => ({
    field: 'details',
    headerName: '',
    cellRenderer: 'seeDetailsCellRenderer',
    suppressHeaderMenuButton: true,
    editable: false,
    suppressNavigable: true,
    width: allowInscriptionSelection ? 85 : 60,
    suppressColumnsToolPanel: true,
    resizable: false,
    cellStyle: { paddingLeft: 0, paddingRight: 0 },
    suppressMovable: true,
    sortable: true,
    sortingOrder: ['desc', null],
    comparator: (valueA, valueB) => (valueA > valueB ? 1 : -1),
    headerCheckboxSelection: allowInscriptionSelection,
    headerCheckboxSelectionFilteredOnly: allowInscriptionSelection,
    checkboxSelection: allowInscriptionSelection,
});

export const getAmountColumn = (
    t: TranslationT,
    budgetProposals: BudgetProposalT[],
    statusesSettings: StatusesSettingsT,
    exercice?: number,
    phase?: BudgetPhases,
): ColDef => {
    const amountInputProps = {
        startAdornment,
        inputComponent: NumberFormatCustom,
    };
    return {
        field: 'amount',
        headerName: t('budgetProposal.inscriptionAmount', {
            currentYear: exercice ?? '',
        }),
        sortable: true,
        cellRenderer: 'textFieldCellRenderer',
        valueGetter: (params) =>
            inscriptionPhaseFieldValueGetter(
                params,
                statusesSettings,
                budgetProposals,
                phase,
            ),
        valueFormatter: ({ value }) => value?.text ?? '',
        context: {
            excelValueFormatter: ({ value }: ValueFormatterParams): number =>
                value?.text,
        },
        cellClass: CURRENCY_EXCEL_CLASSNAMES,
        type: 'numericColumn',
        cellRendererParams: {
            isEnabled: getIsEditablePhase,
            defaultValue: 0,
            valueTransformer: parseNumericInput,
            textFieldProps: {
                placeholder: t('budgetProposal.amountPlaceholder'),
                InputProps: amountInputProps,
            },
        },
        filterValueGetter: (params) =>
            getInscriptionPhaseFieldValue(
                params.data.inscription,
                params.colDef.field as InscriptionGenericPhaseFieldT,
                budgetProposals,
                phase,
            ),
        width: 200,
        filter: 'agNumberColumnFilter',
        floatingFilterComponent: 'numberFloatingFilter',
        enableRowGroup: true,
        aggFunc: 'sumExcludeRejected',
        comparator: inscriptionTextComparator,
    };
};

export const getCommentColumn = (
    t: TranslationT,
    budgetProposals: BudgetProposalT[],
    statusesSettings: StatusesSettingsT,
    phase?: BudgetPhases,
): ColDef => ({
    field: 'comment',
    headerName: t('common.label'),
    sortable: true,
    cellRenderer: 'textFieldCellRenderer',
    valueGetter: (params) =>
        inscriptionPhaseFieldValueGetter(
            params,
            statusesSettings,
            budgetProposals,
            phase,
        ),
    valueFormatter: ({ value }) => value?.text || '',
    cellRendererParams: {
        isEnabled: getIsEditablePhase,
        textFieldProps: {
            placeholder: t('budgetProposal.commentPlaceholder'),
            multiline: true,
            rows: 2,
            width: '100%',
        },
    },
    minWidth: 400,
    filter: 'agTextColumnFilter',
    floatingFilterComponent: 'textFloatingFilter',
    filterValueGetter: (params) =>
        getInscriptionPhaseFieldValue(
            params.data.inscription,
            params.colDef.field as InscriptionGenericPhaseFieldT,
            budgetProposals,
            phase,
        ),
    enableRowGroup: true,
    comparator: inscriptionTextComparator,
});

export const getInscriptionsGridColumns = (
    t: TranslationT,
    budgetProposals: BudgetProposalT[],
    statusesSettings: StatusesSettingsT,
    imputationAsString: null | ((arg1: InscriptionT) => string),
    allowInscriptionSelection: boolean,
    projects: Project[],
    exercice?: number,
    phase?: BudgetPhases,
): ColDef[] => {
    const columns = [
        getDetailsColumn(allowInscriptionSelection),
        getStatusColumn(t, budgetProposals, phase),
        getAmountColumn(t, budgetProposals, statusesSettings, exercice, phase),
        getCommentColumn(t, budgetProposals, statusesSettings, phase),
        getProjectColumn(t, projects),
        getActionsColumn(budgetProposals),
    ];
    if (imputationAsString !== null) {
        columns.push(getImputationColumn(t, imputationAsString));
    }

    return columns;
};

export const getInscriptionsGridColumnsWithPhase = (
    phase: BudgetPhases,
    t: TranslationT,
    budgetProposals: BudgetProposalT[],
    statusesSettings: StatusesSettingsT,
): ColDef[] => {
    const originalInscriptionColumn = {
        field: 'originalInscription',
        headerName: t('budgetProposal.yearInscription'),
        sortable: true,
        valueGetter: ({ data }: { data: InscriptionWithMetadata }) => ({
            text: getOriginalOrCurrentInscriptionValue(
                data.inscription,
                phase,
                t,
            ),
            isRejected: isInscriptionRejected(
                data.inscription,
                getInscriptionField(
                    data.inscription,
                    budgetProposals,
                    'status',
                ),
                phase,
                statusesSettings,
            ),
        }),
        cellRenderer: 'arbitrageInscriptionCellRenderer',
        comparator: inscriptionTextComparator,
        filter: 'agTextColumnFilter',
        type: 'numericColumn',
        floatingFilterComponent: 'textFloatingFilter',
        filterValueGetter: ({ data }: { data: InscriptionWithMetadata }) =>
            getOriginalOrCurrentInscriptionValue(data.inscription, phase, t),
    };
    return [originalInscriptionColumn];
};

export const getInscriptionGridPhaseColumn = (t: TranslationT): ColDef => ({
    field: 'phase',
    comparator: phaseComparator,
    cellRenderer: 'phaseCellRenderer',
    valueGetter: ({ data }: { data: InscriptionWithMetadata }) => {
        return data?.budgetProposalPhase;
    },
    valueFormatter: ({ value }) => t(`budgetProposal.${value}`),
    enableRowGroup: true,
    sortable: true,
});

export const getInscriptionGridBudgetProposalColumn = (
    t: TranslationT,
    budgetProposals: BudgetProposalT[],
): ColDef => ({
    field: 'budgetProposal',
    headerName: t('secondaryBudgetAxisElement360.budgetProposal'),
    valueGetter: ({ data }) => {
        if (!data) return null;
        const budgetProposal = getBudgetProposalFromInscription(
            data.inscription,
            budgetProposals,
        );
        return budgetProposal.name;
    },

    enableRowGroup: true,
    sortable: true,
});

/**
 * Helper function to sort columns based on their settings.
 *
 * @param cols - The column definitions to be sorted.
 * @param columnsSettings - User-defined settings for columns (visibility, width, grouping, etc.).
 * @returns A sorted array of column definitions if indices are present, otherwise the original array.
 */
const sortInscriptionsColumns = (
    colDefs: ColDef[],
    columnsSettings: FieldSettingsT[],
): ColDef[] => {
    const hasIndexSettings = colDefs.every((col) => {
        const colSettings = getColumnSettings(col, columnsSettings);
        return colSettings && typeof colSettings.index === 'number';
    });

    if (!hasIndexSettings) {
        return colDefs;
    }

    return colDefs.sort((a, b) => {
        const aSettings = getColumnSettings(a, columnsSettings);
        const bSettings = getColumnSettings(b, columnsSettings);

        if (!aSettings || !bSettings) {
            return 0;
        }

        return aSettings.index - bSettings.index;
    });
};

type CreateColDefsParams = {
    columns: ColDef[];
    columnsSettings: FieldSettingsT[];
    cellRendererParams: (
        column: ColDef,
    ) => OpenButtonCellRendererParams &
        SeeDetailsCellRendererParams &
        StatusCellRendererParams &
        TextFieldCellRendererParams;
};

/**
 * Helper function to generate colDefs for InscriptionsGrid.
 *
 * @param columns - Base column definitions.
 * @param columnsSettings - User-defined settings for columns (visibility, width, grouping, etc.).
 * @param cellRendererParams - Function to generate specific params for column cell renderers.
 * @returns An array of column definitions for the grid.
 */
export const getInscriptionsColDefs = ({
    columns,
    columnsSettings,
    cellRendererParams,
}: CreateColDefsParams): ColDef[] => {
    const colDefs = columns.map((column) => {
        const columnSettings = getColumnSettings(column, columnsSettings);
        return {
            headerName: column.field
                ? translateBlockValue(column.field, true)
                : '',
            ...column,
            field: column.field,
            resizable: true,
            suppressFloatingFilterButton: true,
            menuTabs: [
                'filterMenuTab',
                'generalMenuTab',
                'columnsMenuTab',
            ] as ColumnMenuTab[],
            cellRendererParams: cellRendererParams(column),
            cellStyle: {
                display: 'flex',
                alignItems: 'center',
            },
            suppressKeyboardEvent: () => true, // Prevent arrow keys in text fields

            ...(columnSettings
                ? {
                      hide: columnSettings.hidden,
                      width: columnSettings.width,
                      rowGroup: columnSettings.grouped,
                      rowGroupIndex: columnSettings.groupIndex,
                  }
                : {}),
        };
    });

    return sortInscriptionsColumns(colDefs, columnsSettings);
};
